import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { DownloadIcon } from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';

/**
 * DownloadDropdown
 * ----------------
 * A purple "Download" button that opens a small menu offering the document in
 * either PDF or DOCX format. Shared by the ADHD Additional Documents panel and
 * the Adult Autism Documents Dashboard so both products download reports the
 * same way (one implementation, not two).
 *
 * Behaviour notes (why the event plumbing looks the way it does):
 * 1. stopPropagation on the trigger and content: these dropdowns are rendered
 *    inside clickable document rows, so we must stop clicks bubbling up to the
 *    row (which would otherwise navigate/toggle the row).
 * 2. preventDefault in onSelect: Radix closes the menu on select by default;
 *    we prevent that so the click resolves into the download call cleanly, and
 *    onCloseAutoFocus is prevented so focus doesn't jump unexpectedly.
 * 3. When isDownloading is true the trigger is disabled and shows a spinner so
 *    the clinician can't fire a second download mid-flight.
 *
 * @param {Object}   props
 * @param {(format: 'pdf' | 'docx') => void} props.onDownload  Called with the chosen format.
 * @param {boolean}  props.isDownloading  Disables the trigger + shows a spinner.
 * @param {string}   [props.label]        Button label (e.g. "Download Report").
 */
export default function DownloadDropdown({ onDownload, isDownloading, label = 'Download' }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        disabled={isDownloading}
        className="bg-violet-800 text-white hover:bg-violet-700 disabled:bg-violet-400 
          flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 
          rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 
          focus:ring-offset-2"
        // Stop event propagation so a click on the trigger doesn't also trigger
        // the surrounding document row's click handler.
        onClick={(e) => e.stopPropagation()}
      >
        {isDownloading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Downloading...
          </>
        ) : (
          <>
            <DownloadIcon className="w-4 h-4 mr-2" />
            {label}
            <ChevronDownIcon className="w-4 h-4 ml-2" />
          </>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black 
            ring-opacity-5 focus:outline-none animate-fade-in-down"
          sideOffset={5}
          align="end"
          alignOffset={0}
          // Stop propagation on the menu content itself so clicks inside the
          // menu never reach the underlying row.
          onClick={(e) => e.stopPropagation()}
          // Prevent focus jumping back to the trigger when the menu closes.
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenu.Item
            className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 
              hover:text-gray-900 cursor-pointer focus:bg-gray-100 focus:outline-none"
            onSelect={(e) => {
              // Keep the menu from auto-closing before the handler runs.
              if (e && e.preventDefault) e.preventDefault();
              onDownload('pdf');
            }}
          >
            Download as PDF
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 
              hover:text-gray-900 cursor-pointer focus:bg-gray-100 focus:outline-none"
            onSelect={(e) => {
              if (e && e.preventDefault) e.preventDefault();
              onDownload('docx');
            }}
          >
            Download as DOCX
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
